import caretRangeFromPoint from '../utils/caretRangeFromPoint';
import clearSelection from '../utils/clearSelection';
import { getIsCurrentlyUsingStylus } from '../utils/detectIfIsCurrentlyUsingStylus';
import getClosestHTMLElement from '../utils/getClosestHTMLElement';
import { shouldIgnoreStylusTouchInThisTarget } from '../utils/stylusEvents';
// eslint-disable-next-line import/no-cycle
import * as selectionEmulation from './selectionEmulation';

export class ImmediateDragTextSelection {
  private _isSelecting: boolean;
  private _isPotentialSelection: boolean;
  private _pxsMovementThresholdToTriggerSelection = 10;
  private _startPoint: { x: number; y: number; } | null;
  private _startRange: Range | null;
  private _handleTouchStartRef: (e: TouchEvent) => void;
  private _handleTouchMoveRef: (e: TouchEvent) => void;
  private _handleTouchEndRef: (e: TouchEvent) => void;
  private _touchMovePreventScrollListener: (e: TouchEvent) => void;

  constructor() {
    this._isSelecting = false;
    this._isPotentialSelection = false;
    this._startPoint = null;
    this._startRange = null;

    /* eslint-disable no-constructor-bind/no-constructor-bind */
    this._handleTouchStartRef = this._handleTouchStart.bind(this);
    this._handleTouchMoveRef = this._handleTouchMove.bind(this);
    this._handleTouchEndRef = this._endSelection.bind(this);
    this._touchMovePreventScrollListener = this._preventTouchMoveScroll.bind(this);
    /* eslint-enable no-constructor-bind/no-constructor-bind */
  }

  init() {
    this._preventUsingStylusJustToScroll();

    // Using touch events instead of pointer events because:
    // 1. Pointer events fire when the stylus hovers near the screen (we only want actual contact)
    // 2. `pointerdown` doesn't consistently fire when quickly re-touching after lifting the stylus and keeping it in hover range
    document.addEventListener('touchstart', this._handleTouchStartRef, { passive: false });
    document.addEventListener('touchmove', this._handleTouchMoveRef, { passive: false });
    document.addEventListener('touchend', this._handleTouchEndRef);
    document.addEventListener('touchcancel', this._handleTouchEndRef);
  }

  destroy() {
    document.removeEventListener('touchstart', this._handleTouchStartRef);
    document.removeEventListener('touchmove', this._handleTouchMoveRef);
    document.removeEventListener('touchend', this._handleTouchEndRef);
    document.removeEventListener('touchcancel', this._handleTouchEndRef);
    document.removeEventListener('touchmove', this._touchMovePreventScrollListener);
    selectionEmulation.stopEmulatingSelection();
  }

  // Private methods

  private _preventTouchMoveScroll(event: TouchEvent) {
    if (!getIsCurrentlyUsingStylus() || !this._isSelecting && !this._isPotentialSelection) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  private _preventUsingStylusJustToScroll() {
    document.addEventListener('touchmove', this._touchMovePreventScrollListener, { passive: false });
  }

  private _handleTouchStart(event: TouchEvent) {
    if (!getIsCurrentlyUsingStylus() || !event.target) {
      return;
    }

    const touch = event.touches[0];

    if (!touch) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const target = getClosestHTMLElement(event.target as Node); // claude eslint: event.target in a touch event handler is always a DOM Node; the EventTarget type is broader than what is ever dispatched here

    if (!target || shouldIgnoreStylusTouchInThisTarget(event)) {
      return;
    }

    // Don't start selection immediately, just mark as potential
    this._isPotentialSelection = true;
    this._startPoint = { x: touch.clientX, y: touch.clientY };
  }

  private _handleTouchMove(event: TouchEvent) {
    if (!getIsCurrentlyUsingStylus() || shouldIgnoreStylusTouchInThisTarget(event)) {
      return;
    }

    const touch = event.touches[0];

    if (!touch) {
      return;
    }

    // If we're in potential selection mode, check if we've moved enough to start selection
    if (this._isPotentialSelection && !this._isSelecting && this._startPoint) {
      const deltaX = Math.abs(touch.clientX - this._startPoint.x);
      const deltaY = Math.abs(touch.clientY - this._startPoint.y);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      if (distance >= this._pxsMovementThresholdToTriggerSelection) {
        this._startSelection(event);
      }
    }

    if (this._isSelecting) {
      this._updateSelection(touch.clientX, touch.clientY);
    }
  }

  private _expandRangeToWordBoundary(range: Range): Range {
    const expandedRange = range.cloneRange();

    this._expandToWordStart(expandedRange);
    this._expandToWordEnd(expandedRange);

    return expandedRange;
  }

  private _expandToWordStart(range: Range) {
    const startContainer = range.startContainer;
    let startOffset = range.startOffset;

    if (startContainer.nodeType === Node.TEXT_NODE) {
      const text = startContainer.textContent || '';

      // Move backwards to find word boundary
      while (startOffset > 0 && this._isWordCharacter(text.charAt(startOffset - 1))) {
        startOffset--;
      }

      range.setStart(startContainer, startOffset);
    }
  }

  private _expandToWordEnd(range: Range) {
    const endContainer = range.endContainer;
    let endOffset = range.endOffset;

    if (endContainer.nodeType === Node.TEXT_NODE) {
      const text = endContainer.textContent || '';

      // Move forwards to find word boundary
      while (endOffset < text.length && this._isWordCharacter(text.charAt(endOffset))) {
        endOffset++;
      }

      range.setEnd(endContainer, endOffset);
    }
  }

  private _isWordCharacter(char: string): boolean {
    // Consider letters, numbers, and common word characters
    return /[\w\u00C0-\u024F\u1E00-\u1EFF]/.test(char);
  }

  private _startSelection(touch: TouchEvent) {
    if (!this._startPoint) {
      return;
    }

    clearSelection();
    this._isSelecting = true;
    this._isPotentialSelection = false;
    selectionEmulation.startEmulatingSelection({ lastPointerDownEvent: touch });

    // Use the original start point, not current touch position
    const x = this._startPoint.x;
    const y = this._startPoint.y;

    // Get the character range at the touch point
    const range = caretRangeFromPoint(x, y);

    if (range) {
      const expandedRange = this._expandRangeToWordBoundary(range.cloneRange());
      this._startRange = expandedRange;

      const selection = window.getSelection();

      if (!selection) {
        return;
      }

      selection.addRange(expandedRange);
    }
  }

  private _updateSelection(x: number, y: number) {
    if (!this._isSelecting || !this._startPoint || !this._startRange) {
      return;
    }

    const selection = window.getSelection();

    if (!selection) {
      return;
    }

    const endRange = caretRangeFromPoint(x, y);

    if (!endRange) {
      return;
    }

    const expandedEndRange = this._expandRangeToWordBoundary(endRange);

    // Create a new range that spans from start to current position
    const newRange = document.createRange();

    const comparison = this._startRange.compareBoundaryPoints(Range.START_TO_START, expandedEndRange);

    if (comparison <= 0) {
      // Forward selection (left to right, top to bottom)
      newRange.setStart(this._startRange.startContainer, this._startRange.startOffset);
      newRange.setEnd(expandedEndRange.endContainer, expandedEndRange.endOffset);
    } else {
      // Backward selection (right to left, bottom to top)
      newRange.setStart(expandedEndRange.startContainer, expandedEndRange.startOffset);
      newRange.setEnd(this._startRange.endContainer, this._startRange.endOffset);
    }

    selection.removeAllRanges();
    selection.addRange(newRange);
  }

  private _endSelection() {
    if (!this._isSelecting && !this._isPotentialSelection) {
      return;
    }

    this._isSelecting = false;
    this._isPotentialSelection = false;
    this._startPoint = null;
    this._startRange = null;
    selectionEmulation.stopEmulatingSelection();
  }
}

export function initializeImmediateDragTextSelectionHandler() {
  const immediateDragTextSelection = new ImmediateDragTextSelection();
  immediateDragTextSelection.init();
  return immediateDragTextSelection;
}
